import {List, Map} from 'immutable'
import {useEffect} from 'react'
import PropTypes from 'prop-types'

import Loader from '../../../../../shared_components/NewLoader'
import LifeStoryItemModal from './LifeStoryItemModal'
import useEverplanEthicalWills from './hooks/useEverplanEthicalWills'
import {usePendingLifeStoryDeletion} from './lifeStoryPendingDeletion'
import {ethicalWillByFirstGroupId} from './lifeStoryTools'
import {firstName} from '../../../../../lib/nameTools'

/**
 * Renders the guided experience's item modal when the viewed response group is
 * tracked by an EthicalWill; otherwise falls back to the classic item view.
 * On another person's everplan the wills come from the everplan-filtered
 * endpoint, which only returns stories the viewer's permissions allow — so a
 * deputy without access simply falls back to the classic view. Kept as a
 * separate component so ethical wills are only fetched when needed.
 */
const LifeStoryItemModalGate = ({context, everplanId, exit, fallback, groupId, itemResponse, ownEverplan = true, ownerships = List(), userConfig}) => {
  const {loadingEthicalWills, ethicalWillsUnavailable, ethicalWills} = useEverplanEthicalWills({everplanId, ownEverplan})
  const pendingDeletion = usePendingLifeStoryDeletion()

  // The group id in the URL can be either of the group's two ids (see
  // itemResponseGroupByGroupId), while EthicalWill tracks the first one, so
  // resolve the URL id to the group's first id before matching.
  const responseInGroup = itemResponse.get('responses', List()).find(response => response.get('group-id').includes(groupId))
  const firstGroupId = responseInGroup ? responseInGroup.getIn(['group-id', 0]) : groupId
  const ethicalWill = ethicalWillByFirstGroupId({ethicalWills, firstGroupId})

  // A story inside its undo-after-delete window is already gone as far as the
  // user is concerned: a stale link (e.g. the back button) into its group view
  // exits rather than reopening the doomed record.
  const deletionPending = Boolean(pendingDeletion && (
    pendingDeletion.firstGroupId === firstGroupId ||
    (ethicalWill && pendingDeletion.ethicalWillId === ethicalWill.get('id'))
  ))

  useEffect(() => {
    if (deletionPending) exit()
  }, [deletionPending, exit])

  if (deletionPending)
    return <Loader loading={true} />

  // A failed fetch means the store can't be trusted to reflect what the
  // viewer may see, so the classic item view is the safe rendering — even
  // over a previously cached will.
  if (ethicalWillsUnavailable)
    return fallback

  // A will already in the redux store — e.g. returning here after closing the
  // full reading experience, which was just rendered from it — shows its modal
  // right away instead of hiding it behind a loader while the mount's refetch
  // revalidates in the background (TK-3082).
  if (ethicalWill) {
    return (
      <LifeStoryItemModal
        context={context}
        ethicalWill={ethicalWill}
        everplanId={everplanId}
        exit={exit}
        groupId={groupId}
        itemResponse={itemResponse}
        ownerFirstName={firstName(ownerships.first() || Map())}
        userConfig={userConfig}
        viewerIsOwner={ownEverplan}
      />
    )
  }

  if (loadingEthicalWills)
    return <Loader loading={true} />

  return fallback
}

LifeStoryItemModalGate.propTypes = {
  context: PropTypes.string,
  everplanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exit: PropTypes.func,
  fallback: PropTypes.node,
  groupId: PropTypes.string,
  itemResponse: PropTypes.instanceOf(Map),
  ownEverplan: PropTypes.bool,
  ownerships: PropTypes.instanceOf(List),
  userConfig: PropTypes.instanceOf(Map)
}

export default LifeStoryItemModalGate
